import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminLocale, t } from '../i18n';
import {
  deleteMailboxMessage,
  fetchMailboxMessage,
  fetchMailboxMessages,
  fetchMailboxStatus,
  markMailboxMessageRead,
  replyMailboxMessage,
  sendMailboxMessage,
} from '../api/emailApi';

const EMPTY_COMPOSE = { to: '', cc: '', subject: '', body_text: '' };

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(getAdminLocale(), {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function dateBoundaryIso(dateText, { endExclusive = false } = {}) {
  const raw = String(dateText || '').trim();
  if (!raw) return '';
  const date = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  if (endExclusive) date.setDate(date.getDate() + 1);
  return date.toISOString();
}

function todayDateText() {
  const now = new Date();
  const local = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
  return local.toISOString().slice(0, 10);
}

function formatAddress(item) {
  if (!item) return '-';
  const name = String(item.name || '').trim();
  const address = String(item.address || '').trim();
  if (name && address) return `${name} <${address}>`;
  return address || name || '-';
}

function joinAddresses(items) {
  if (!Array.isArray(items) || items.length === 0) return '-';
  return items.map(formatAddress).join(', ');
}

function htmlToPlainText(content) {
  const raw = String(content || '');
  if (!raw) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'text/html');
    doc.querySelectorAll('script,style,noscript,iframe,object,embed').forEach((node) => node.remove());
    return String(doc.body?.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  } catch (_) {
    return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function mailboxErrorInfo(error, fallback) {
  const data = error?.response?.data || {};
  return {
    message: data.message || error?.message || fallback,
    hint: data.hint || '',
    code: data.code || '',
    details: data.details && typeof data.details === 'object' ? data.details : null,
  };
}

function FolderButton({ active, label, count, badge, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition ${active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
    >
      <span>{label}</span>
      <span className="flex items-center gap-2">
        {badge > 0 ? <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-white text-gray-900' : 'bg-blue-600 text-white'}`}>{badge}</span> : null}
        <span className={`text-xs ${active ? 'text-gray-300' : 'text-gray-400'}`}>{Number(count || 0).toLocaleString(getAdminLocale())}</span>
      </span>
    </button>
  );
}

function MessageRow({ message, folder, selected, onClick }) {
  const counterpart = folder === 'inbox'
    ? formatAddress(message.from)
    : joinAddresses(message.to);
  const date = folder === 'inbox' ? message.received_at : message.sent_at;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full border-b border-gray-100 p-4 text-left transition hover:bg-gray-50 ${selected ? 'bg-blue-50/70' : 'bg-white'}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1.5 w-2 flex-shrink-0">
          {folder === 'inbox' && !message.is_read ? <span className="block h-2 w-2 rounded-full bg-blue-600" /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className={`truncate text-sm ${folder === 'inbox' && !message.is_read ? 'font-extrabold text-gray-950' : 'font-semibold text-gray-700'}`}>{counterpart}</div>
            <div className="flex-shrink-0 text-[11px] text-gray-400">{formatDate(date)}</div>
          </div>
          <div className={`mt-1 truncate text-sm ${folder === 'inbox' && !message.is_read ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{message.subject || t('(제목 없음)')}</div>
          <div className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{message.preview || t('미리보기 내용이 없습니다.')}</div>
          {message.has_attachments ? <div className="mt-2 text-[11px] font-semibold text-gray-500">📎 {t('첨부파일 있음')}</div> : null}
        </div>
      </div>
    </button>
  );
}

function Composer({ mode, selectedMessage, form, setForm, sending, onCancel, onSend }) {
  const isReply = mode === 'reply';
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  return (
    <div className="flex h-full min-h-[560px] flex-col bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="font-bold text-gray-900">{isReply ? t('답장 작성') : t('새 메일 작성')}</h3>
          <p className="mt-1 text-xs text-gray-500">support@nightpokerclub.com</p>
        </div>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50">{t('취소')}</button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        {isReply ? (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">{t('받는 사람')}</label>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{formatAddress(selectedMessage?.from)}</div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">{t('제목')}</label>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">Re: {selectedMessage?.subject || t('(제목 없음)')}</div>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">{t('받는 사람')}</label>
              <input value={form.to} onChange={(event) => update('to', event.target.value)} placeholder="user@example.com" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
              <div className="mt-1 text-[11px] text-gray-400">{t('여러 주소는 쉼표로 구분할 수 있습니다.')}</div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">CC</label>
              <input value={form.cc} onChange={(event) => update('cc', event.target.value)} placeholder="cc@example.com" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">{t('제목')}</label>
              <input value={form.subject} maxLength={255} onChange={(event) => update('subject', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </div>
          </>
        )}
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-500">{t('내용')}</label>
          <textarea value={form.body_text} maxLength={50000} onChange={(event) => update('body_text', event.target.value)} rows={16} placeholder={t('메일 내용을 입력하세요.')} className="w-full resize-y rounded-lg border border-gray-300 px-3 py-3 text-sm leading-6 outline-none focus:border-blue-500" />
          <div className="mt-1 text-right text-[11px] text-gray-400">{form.body_text.length.toLocaleString(getAdminLocale())} / 50,000</div>
        </div>
      </div>

      <div className="flex justify-end border-t border-gray-100 p-4">
        <button type="button" onClick={onSend} disabled={sending} className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
          {sending ? t('발송 중...') : t('메일 발송')}
        </button>
      </div>
    </div>
  );
}

export default function MailboxPanel() {
  const [status, setStatus] = useState(null);
  const [folder, setFolder] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [mailboxError, setMailboxError] = useState(null);
  const [composeMode, setComposeMode] = useState(null);
  const [composeForm, setComposeForm] = useState(EMPTY_COMPOSE);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  const loadStatus = useCallback(async () => {
    const data = await fetchMailboxStatus();
    setStatus(data);
    return data;
  }, []);

  const loadMessages = useCallback(async (nextFolder, filters = {}) => {
    const data = await fetchMailboxMessages(nextFolder, 100, filters);
    setMessages(Array.isArray(data?.messages) ? data.messages : []);
    return data;
  }, []);

  const refreshAll = useCallback(async ({ keepSelection = true } = {}) => {
    setLoading(true);
    setMailboxError(null);
    try {
      await Promise.all([loadStatus(), loadMessages(folder, dateFilter)]);
      if (!keepSelection) {
        setSelectedId(null);
        setSelectedMessage(null);
      }
    } catch (error) {
      setMailboxError(mailboxErrorInfo(error, t('Microsoft 365 메일함에 연결하지 못했습니다.')));
    } finally {
      setLoading(false);
    }
  }, [dateFilter, folder, loadMessages, loadStatus]);

  useEffect(() => { refreshAll({ keepSelection: false }); }, [refreshAll]);

  const switchFolder = (nextFolder) => {
    if (nextFolder === folder) return;
    setFolder(nextFolder);
    setSelectedId(null);
    setSelectedMessage(null);
    setComposeMode(null);
    setComposeForm(EMPTY_COMPOSE);
    setMailboxError(null);
  };

  const applyDateFilter = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      alert(t('종료 날짜는 시작 날짜보다 빠를 수 없습니다.'));
      return;
    }
    setDateFilter({
      from: dateBoundaryIso(dateFrom),
      to: dateBoundaryIso(dateTo, { endExclusive: true }),
    });
    setSelectedId(null);
    setSelectedMessage(null);
    setComposeMode(null);
  };

  const clearDateFilter = () => {
    const alreadyClear = !dateFrom && !dateTo && !dateFilter.from && !dateFilter.to;
    setDateFrom('');
    setDateTo('');
    setDateFilter({ from: '', to: '' });
    setSelectedId(null);
    setSelectedMessage(null);
    setComposeMode(null);
    if (alreadyClear) refreshAll({ keepSelection: false });
  };

  const setTodayFilter = () => {
    const today = todayDateText();
    setDateFrom(today);
    setDateTo(today);
    setDateFilter({
      from: dateBoundaryIso(today),
      to: dateBoundaryIso(today, { endExclusive: true }),
    });
    setSelectedId(null);
    setSelectedMessage(null);
    setComposeMode(null);
  };

  const openMessage = async (message) => {
    setSelectedId(message.id);
    setComposeMode(null);
    setMessageLoading(true);
    try {
      const data = await fetchMailboxMessage(message.id);
      setSelectedMessage(data?.message || null);
      if (folder === 'inbox' && !message.is_read) {
        try {
          await markMailboxMessageRead(message.id, true);
          setMessages((current) => current.map((row) => row.id === message.id ? { ...row, is_read: true } : row));
          setStatus((current) => current ? {
            ...current,
            inbox: { ...current.inbox, unread: Math.max(0, Number(current.inbox?.unread || 0) - 1) },
          } : current);
        } catch (_) {
          // Reading the message is still useful even when the read-state update fails.
        }
      }
    } catch (error) {
      alert(errorMessage(error, t('메일 내용을 불러오지 못했습니다.')));
    } finally {
      setMessageLoading(false);
    }
  };

  const startCompose = () => {
    setSelectedId(null);
    setSelectedMessage(null);
    setComposeForm(EMPTY_COMPOSE);
    setComposeMode('compose');
  };

  const startReply = () => {
    if (!selectedMessage) return;
    setComposeForm({ ...EMPTY_COMPOSE, body_text: '' });
    setComposeMode('reply');
  };

  const removeSelectedMessage = async () => {
    if (!selectedMessage?.id || deleting) return;
    if (!window.confirm(t('선택한 메일을 삭제하시겠습니까?'))) return;
    setDeleting(true);
    try {
      await deleteMailboxMessage(selectedMessage.id);
      setSelectedId(null);
      setSelectedMessage(null);
      setComposeMode(null);
      await Promise.all([
        loadStatus().catch(() => null),
        loadMessages(folder, dateFilter).catch(() => null),
      ]);
    } catch (error) {
      alert(errorMessage(error, t('메일 삭제에 실패했습니다.')));
    } finally {
      setDeleting(false);
    }
  };

  const sendCompose = async () => {
    if (!composeForm.body_text.trim()) {
      alert(t('메일 내용을 입력해 주세요.'));
      return;
    }
    if (composeMode === 'compose' && (!composeForm.to.trim() || !composeForm.subject.trim())) {
      alert(t('받는 사람과 제목을 입력해 주세요.'));
      return;
    }
    setSending(true);
    try {
      let result;
      if (composeMode === 'reply') {
        result = await replyMailboxMessage(selectedMessage.id, { body_text: composeForm.body_text.trim() });
      } else {
        result = await sendMailboxMessage({
          to: composeForm.to,
          cc: composeForm.cc,
          subject: composeForm.subject.trim(),
          body_text: composeForm.body_text.trim(),
        });
      }
      alert(result?.message || t('메일 발송을 요청했습니다.'));
      setComposeMode(null);
      setComposeForm(EMPTY_COMPOSE);
      await loadStatus().catch(() => null);
      if (folder === 'sent') await loadMessages('sent', dateFilter).catch(() => null);
    } catch (error) {
      alert(errorMessage(error, t('메일 발송에 실패했습니다.')));
    } finally {
      setSending(false);
    }
  };

  const bodyText = useMemo(() => {
    const body = selectedMessage?.body;
    if (!body) return selectedMessage?.preview || '';
    return body.content_type === 'html' ? htmlToPlainText(body.content) : String(body.content || '');
  }, [selectedMessage]);

  return (
    <div className="space-y-4">
      {mailboxError ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-bold">{t('Microsoft 365 메일함 연결 오류')}</div>
          <div className="mt-1 break-words leading-6">{mailboxError.message}</div>
          {mailboxError.code ? (
            <div className="mt-2 text-[11px] font-semibold text-amber-700">CODE: {mailboxError.code}</div>
          ) : null}
          {mailboxError.hint ? (
            <div className="mt-2 break-words text-xs leading-5 text-amber-700">{mailboxError.hint}</div>
          ) : null}
          {mailboxError.details?.credential_source ? (
            <div className="mt-2 break-all text-[11px] leading-5 text-amber-700">
              OAuth source: {mailboxError.details.credential_source}
              {mailboxError.details.email_worker_env_path ? ` · ${mailboxError.details.email_worker_env_path}` : ''}
            </div>
          ) : null}
          {Array.isArray(mailboxError.details?.token_roles) ? (
            <div className="mt-1 break-words text-[11px] leading-5 text-amber-700">
              Token roles: {mailboxError.details.token_roles.length ? mailboxError.details.token_roles.join(', ') : '(none)'}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(320px,0.9fr)_minmax(420px,1.35fr)]">
        <aside className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <button type="button" onClick={startCompose} className="mb-4 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700">＋ {t('새 메일')}</button>
          <div className="space-y-1">
            <FolderButton active={folder === 'inbox'} label={t('받은편지함')} count={status?.inbox?.total} badge={status?.inbox?.unread} onClick={() => switchFolder('inbox')} />
            <FolderButton active={folder === 'sent'} label={t('보낸편지함')} count={status?.sent?.total} badge={0} onClick={() => switchFolder('sent')} />
          </div>
          <div className="mt-5 border-t border-gray-100 pt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Microsoft 365</div>
            <div className="mt-2 break-all text-xs font-semibold text-gray-700">{status?.mailbox || 'support@nightpokerclub.com'}</div>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
              <span className={`h-2 w-2 rounded-full ${status?.connected ? 'bg-emerald-500' : 'bg-gray-300'}`} />
              {status?.connected ? t('연결됨') : t('연결 확인 중')}
            </div>
          </div>
          <button type="button" onClick={() => refreshAll({ keepSelection: true })} disabled={loading} className="mt-5 w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">{loading ? t('새로고침 중...') : t('메일함 새로고침')}</button>
        </aside>

        <section className="min-h-[650px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-bold text-gray-900">{folder === 'inbox' ? t('받은편지함') : t('보낸편지함')}</h2>
              <span className="text-xs text-gray-400">{messages.length.toLocaleString(getAdminLocale())}{t('건 표시')}</span>
            </div>
            <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="min-w-0 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-500" aria-label={t('시작 날짜')} />
              <span className="text-xs text-gray-400">~</span>
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="min-w-0 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-500" aria-label={t('종료 날짜')} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={applyDateFilter} className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-black">{t('날짜 조회')}</button>
              <button type="button" onClick={setTodayFilter} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">{t('오늘')}</button>
              <button type="button" onClick={clearDateFilter} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">{t('전체')}</button>
            </div>
          </div>
          <div className="max-h-[700px] overflow-y-auto">
            {loading ? <div className="p-10 text-center text-sm text-gray-400">{t('메일을 불러오는 중...')}</div> : null}
            {!loading && messages.map((message) => (
              <MessageRow key={message.id} message={message} folder={folder} selected={selectedId === message.id} onClick={() => openMessage(message)} />
            ))}
            {!loading && messages.length === 0 && !mailboxError ? <div className="p-12 text-center text-sm text-gray-400">{t('표시할 메일이 없습니다.')}</div> : null}
          </div>
        </section>

        <section className="min-h-[650px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {composeMode ? (
            <Composer mode={composeMode} selectedMessage={selectedMessage} form={composeForm} setForm={setComposeForm} sending={sending} onCancel={() => setComposeMode(null)} onSend={sendCompose} />
          ) : messageLoading ? (
            <div className="p-12 text-center text-sm text-gray-400">{t('메일 내용을 불러오는 중...')}</div>
          ) : selectedMessage ? (
            <div className="flex h-full min-h-[650px] flex-col">
              <div className="border-b border-gray-100 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="break-words text-xl font-extrabold text-gray-950">{selectedMessage.subject || t('(제목 없음)')}</h2>
                    <div className="mt-3 space-y-1 text-xs leading-5 text-gray-500">
                      <div><span className="font-semibold text-gray-700">{t('보낸 사람')}:</span> {formatAddress(selectedMessage.from)}</div>
                      <div><span className="font-semibold text-gray-700">{t('받는 사람')}:</span> {joinAddresses(selectedMessage.to)}</div>
                      {selectedMessage.cc?.length ? <div><span className="font-semibold text-gray-700">CC:</span> {joinAddresses(selectedMessage.cc)}</div> : null}
                      <div><span className="font-semibold text-gray-700">{t('시간')}:</span> {formatDate(folder === 'inbox' ? selectedMessage.received_at : selectedMessage.sent_at)}</div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {folder === 'inbox' ? <button type="button" onClick={startReply} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white hover:bg-black">↩ {t('답장')}</button> : null}
                    <button type="button" onClick={removeSelectedMessage} disabled={deleting} className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50">{deleting ? t('삭제 중...') : `🗑 ${t('삭제')}`}</button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7 text-gray-800">{bodyText || t('메일 본문이 없습니다.')}</pre>
                {selectedMessage.has_attachments ? (
                  <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">📎 {t('첨부파일이 포함된 메일입니다. 현재 어드민 UI에서는 첨부 여부만 표시합니다.')}</div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex min-h-[650px] items-center justify-center p-8 text-center">
              <div>
                <div className="text-4xl">✉️</div>
                <div className="mt-3 font-bold text-gray-700">{t('메일을 선택하세요')}</div>
                <div className="mt-1 text-sm text-gray-400">{t('받은 메일을 확인하거나 새 메일을 작성할 수 있습니다.')}</div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
